<script>
import _ from 'lodash';

// Padding to keep between the bottom of the content and the bottom of the viewport
const BOTTOM_VIEWPORT_PADDING = 32;

let scrollDirection = null; // true for down, false for up, null for neither
let lastScrollPosition = 0;
const determineScrollDirection = () => {
  // Store the previous scroll position
  const currentScrollPosition = window.scrollY;

  if (currentScrollPosition === lastScrollPosition) {
    scrollDirection = null;
  } else {
    scrollDirection = currentScrollPosition > lastScrollPosition;
  }
  lastScrollPosition = currentScrollPosition;
};

export default {
  props: {
    // This is the minimum space to keep between the top of the content and the top of the *window*
    minTopSpace: {
      type: Number,
      default: 0,
    },
    // This is the minimum space to keep between the bottom of the content and the bottom of the *page*
    minBottomSpace: {
      type: Number,
      default: 0, // Minimum distance from the bottom of the page
    },
  },

  data() {
    return {
      // A flag to indicate whether the container is currently in a sticky state
      _rafHandle: null,
    };
  },

  methods: {
    handleScroll: function () {
      if (!this._rafHandle) {
        this._rafHandle = requestAnimationFrame(() => {
          this.recalculate();
          this._rafHandle = null;
        });
      }
    },

    recalculate() {
      // Update the current scroll position
      const currentScrollY = window.scrollY;

      // Get the bounding rectangles of the scroll container and content container
      const containerRect = this.$refs.scrollContainer.getBoundingClientRect();
      const contentRect = this.$refs.contentContainer.getBoundingClientRect();

      // Calculate the top position of the container relative to the document
      const containerTop = containerRect.top + window.scrollY;

      // Get the height of the viewport
      const viewportHeight = window.innerHeight;

      const pageHeight = document.documentElement.scrollHeight;
      const contentHeight = contentRect.height;

      // If the page height is equal (or less) than the content we'll never actually have to adjust it
      if (pageHeight <= this.minTopSpace + contentHeight + this.minBottomSpace)
        return;

      // We calculate a max padding both for the bottom allow provided, as well
      // as based on the size of the container
      const maxPadding =
        pageHeight - this.minTopSpace - contentHeight - this.minBottomSpace;

      // Calculate the padding needed to align the bottom or top of the content
      const paddingForAlignedBottom = Math.max(
        0,
        Math.min(
          maxPadding,
          currentScrollY +
            viewportHeight -
            (containerTop + contentRect.height) -
            BOTTOM_VIEWPORT_PADDING,
        ),
      );
      const paddingForAlignedTop = Math.max(
        0,
        this.minTopSpace + currentScrollY - containerTop,
      );

      // We'll need this a few times
      const paddingElement = this.$refs.padded;

      // Get the current padding-top value of the padded element
      let newElementTop =
        parseInt(
          (paddingElement.style.transform.match(
            /translateY\(([\d.]*)(px)?\)/,
          ) || [])[1],
          10,
        ) || 0;

      // Adjust the padding based on the scroll direction
      if (scrollDirection === true) {
        // Scrolling down
        newElementTop = Math.max(newElementTop, paddingForAlignedBottom);
      } else if (scrollDirection === false) {
        // Scrolling up
        newElementTop = Math.min(newElementTop, paddingForAlignedTop);
      }
      // NOTE: scrollDirection can also be null for indeterminate

      // Update the padding-top only if it has changed
      const newTranslateY = `translateY(${newElementTop}px)`;
      if (!paddingElement.style.transform.includes(newTranslateY)) {
        paddingElement.style.transform = newTranslateY;
      }
    },
  },

  mounted() {
    window.addEventListener('scroll', determineScrollDirection);
    window.addEventListener('scroll', this.handleScroll);
    window.addEventListener('resize', this.handleScroll);
    // Initial position calculation
    this.$nextTick(this.handleScroll);
  },

  beforeUnmount() {
    window.removeEventListener('scroll', determineScrollDirection);
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleScroll);
  },
};
</script>

<template>
  <div class="scroll-container" ref="scrollContainer">
    <div class="padder" ref="padded">
      <div class="content-wrapper" ref="contentContainer">
        <slot />
      </div>
    </div>
  </div>
</template>

<style scoped>
.scroll-container {
  overflow: visible;
}

.padder {
  will-change: transform;
}
</style>
